
import { Component, Vue } from 'vue-property-decorator';
import { Route, NavigationGuardNext } from 'vue-router';
import store from '@/store';
import FondoDiagonal from '@/componentes/UI/FondoDiagonal.vue';
import ProgresoPasos from '@/componentes/ProgresoPasos.vue';
import InformacionPago from '@/componentes/pago/InformacionPago.vue';
import VideoYoutube from '@/componentes/UI/VideoYoutube.vue';
import {
  ResumenI, getResumen, parseDetallesPeriodo, DetallesPeriodoI,
} from '@/assets/api/matricula';
import { ModalidadCuotaI, getModalidadesCuotas } from '@/assets/api/pagare';
import StepCard from '@/componentes/UI/StepCard.vue';
import HeaderPrincipal from "@/componentes/UI/HeaderPrincipal.vue";

@Component({
  components: {
    HeaderPrincipal,
    FondoDiagonal,
    ProgresoPasos,
    InformacionPago,
    VideoYoutube,
    StepCard,
  },
})
export default class Resumen extends Vue {
  loading = true;
  resumen: ResumenI = {} as ResumenI;
  modalidadesCuotas: ModalidadCuotaI[] = [];
  detallesPeriodoMatricula: DetallesPeriodoI = {} as DetallesPeriodoI;
  detallesPeriodoArancel: DetallesPeriodoI = {} as DetallesPeriodoI;

  get esMagister(): boolean {
    return this.$store.getters.esMagister;
  }

  get tieneGratuidad(): boolean {
    const descripcion = this.resumen.matricula.descripcion_beneficios?.toLowerCase() || '';
    const tieneDescripcionGratuidad = descripcion.includes('gratuidad') && descripcion.includes('$0');
    const montoEsCero = this.resumen.matricula.monto_final === 0;
    return tieneDescripcionGratuidad || montoEsCero;
  }

  // Computed properties for colors
  get planColor(): string {
    const colorVar = this.$store.getters.colorPlan;
    if (colorVar.includes('var(')) {
      const colorMap: Record<string, string> = {
        'var(--azul-institucional)': '#375282',
        'var(--verde-institucional)': '#40b99d',
        'var(--celeste-institucional)': '#56c0e8',
        'var(--azul-enfermeria)': '#273470',
        'var(--rojo-obstetricia)': '#AB2326',
        'var(--verde-agronomia)': '#639244',
        'var(--verde-forestal)': '#136E49',
        'var(--naranjo-industrial)': '#F06A26',
        'var(--amarillo-social)': '#F9b81C',
        'var(--celeste-psicologia)': '#00ACB4',
        'var(--rosado-informatica)': '#D60B51',
        'var(--verde-terapia-ocupacional)': '#336666',
        'var(--azul-ing-comercial)': '#006699',
        'var(--naranjo)': '#f27c08',
        'var(--morado-adm-publica)': '#333366',
        'var(--gris-derecho)': '#666666',
        'var(--amarillo-ing-renovables)': '#8c9c27'
      };
      return colorMap[colorVar] || '#375282';
    }
    return colorVar;
  }

  get planTextColor(): string {
    const colorVar = this.$store.getters.colorTextoPlan;
    if (colorVar.includes('var(')) {
      const colorMap: Record<string, string> = {
        'var(--azul-institucional)': '#375282',
        'var(--verde-institucional)': '#40b99d',
        'var(--celeste-institucional)': '#56c0e8',
        'var(--azul-enfermeria)': '#273470',
        'var(--rojo-obstetricia)': '#AB2326',
        'var(--verde-agronomia)': '#639244',
        'var(--verde-forestal)': '#136E49',
        'var(--naranjo-industrial)': '#F06A26',
        'var(--amarillo-social)': '#F9b81C',
        'var(--celeste-psicologia)': '#00ACB4',
        'var(--rosado-informatica)': '#D60B51',
        'var(--verde-terapia-ocupacional)': '#336666',
        'var(--azul-ing-comercial)': '#006699',
        'var(--naranjo)': '#f27c08',
        'var(--morado-adm-publica)': '#333366',
        'var(--gris-derecho)': '#666666',
        'var(--amarillo-ing-renovables)': '#8c9c27'
      };
      return colorMap[colorVar] || '#375282';
    }
    return colorVar;
  }

  // Date formatting methods
  dia = (fecha: Date): string => fecha?.toLocaleString('es-cl', { day: '2-digit' });
  mes = (fecha: Date): string => fecha?.toLocaleString('es-cl', { month: 'long' });

  fecha(fecha: Date): string {
    return `${this.dia(fecha)} de ${this.mes(fecha)}`;
  }

  // Video mapping by career plan
  videoCarrera: { [plan: number]: string } = {
    1: 'sSJUFJi_W38',
    2: 'sXLsY08WAkw',
    3: 'wOy1kakZHDs',
    4: 'mWHd842atdA',
    5: 'yRyYCkbOsug',
    6: 'NdGGk7Swra4',
    7: 'q5X5zx7g7-Y',
    8: 'WZK-Dpb86Wk',
    22: 'uOgObMyPW3I',
    23: 'mXVVrHJxyWk',
    31: 'b5R9fw-0L_8',
    32: 'S-z9je4VF7w',
    33: 'NUoiUBJ3-po',
  };

  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext<Resumen>) {
    const promesaResumen = getResumen(store.getters.idEstudiante);
    const promesaModalidades = getModalidadesCuotas(
      store.getters.idPlan,
      store.getters.esEstudianteAntiguo ? 1 : 0,
    );
    Promise.all([promesaResumen, promesaModalidades])
      .then(([resumen, modalidades]) => {
        next((vm) => {
          vm.resumen = resumen;
          vm.modalidadesCuotas = modalidades;
          vm.detallesPeriodoMatricula = parseDetallesPeriodo(resumen.matricula.periodo_matricula);
          vm.detallesPeriodoArancel = parseDetallesPeriodo(resumen.arancel.periodo_matricula);
          vm.loading = false;
        });
      });
  }

  mounted(): void {
    this.$nextTick(() => {
      setTimeout(() => {
        this.scrollToRelevantSection();
      }, 500);
    });
  }

  scrollToRelevantSection(): void {
    if (this.estadoMatricula === 1 && this.$refs.congratulationsSection) {
      const element = this.$refs.congratulationsSection as HTMLElement;
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  get video(): string {
    return this.videoCarrera[this.$store.getters.idPlan];
  }

  get puedeModificarPagos(): boolean {
    return this.resumen.hay_periodo_abierto && this.resumen.habilitado;
  }

  get estadoMatricula(): number {
    return this.resumen.matricula?.estado;
  }

  get estadoDisplayMatricula(): string {
    return this.resumen.matricula?.orden_compra?.estado_pago_display;
  }

  get montoMatricula(): number {
    return this.resumen.matricula?.monto_final;
  }

  get estadoArancel(): number {
    if (!this.resumen.arancel.pago_cuotas && this.resumen.arancel.orden_compra) {
      return this.resumen.arancel.orden_compra.estado_pago;
    }
    if (this.resumen.arancel.pagare) {
      return this.resumen.arancel.pagare.estado;
    }
    return 0;
  }

  get estadoDisplayArancel(): string {
    if (!this.resumen.arancel.pago_cuotas && this.resumen.arancel.orden_compra) {
      return this.resumen.arancel.orden_compra.estado_pago_display;
    }
    if (this.resumen.arancel.pagare) {
      return this.resumen.arancel.pagare.estado_display;
    }
    return '???';
  }

  get montoArancel(): number | string {
    if (!this.resumen.arancel.pago_cuotas && this.resumen.arancel.orden_compra) {
      return this.resumen.arancel.orden_compra.monto_total
        - this.resumen.arancel.orden_compra.total_descuento_beneficios;
    }
    if (this.resumen.arancel.pagare) {
      return this.resumen.arancel.pagare.monto_arancel;
    }
    return '???';
  }

  get procesoListo(): boolean {
    return this.estadoMatricula === 1
      && this.estadoArancel === 1
      && this.$store.getters.esPostulante;
  }

  salir(): void {
    this.$store.commit('logout');
    this.$router.push({ name: this.$store.getters.linkInicio });
  }
}
